/**
 * AI page — UI-free rules: the prompt library, history grouping, the "Based on …" source line,
 * table extraction for CSV, and friendly error text.
 */
import { formatDate, formatDateTime, formatTime, toDateInputValue } from '@/lib/format';
import { csvRow } from './csv';

/* ------------------------------------------------------------------ prompts */

/**
 * The prompt library, by domain. `[...]` marks a part to replace — it is selected after the
 * prompt fills the box. Nothing is sent until the person presses Send.
 */
export const PROMPT_LIBRARY = [
  {
    key: 'projects',
    label: 'Projects',
    prompts: [
      'Give me a project summary',
      'Which projects are active?',
      'Show total project value by status',
      'Which projects have no data in the LPO/LOI stage?',
      'Which projects have no data in the [Material Submittal] stage?',
      'What is missing at the active projects?',
    ],
  },
  {
    key: 'invoicing',
    label: 'Invoicing',
    invoicing: true,
    prompts: [
      'Give me the invoicing summary',
      'Show the largest pending invoices',
      'Which invoices are not started?',
      'How much is submitted and still unpaid per project?',
    ],
  },
  {
    key: 'tasks',
    label: 'Tasks & actions',
    prompts: [
      'Open tasks due this week',
      'Which open tasks have no one assigned?',
      { text: 'Create a task for [project name]: [what needs to be done]', action: true },
      { text: 'Mark invoice [reference] as Payment Received', action: true, invoicing: true },
    ],
  },
  {
    key: 'supply',
    label: 'Supply',
    prompts: [
      'Late LPOs by supplier',
      'Which stock items are low or out of stock?',
      'Deliveries received this month',
      'Open site requests by project',
    ],
  },
];

export const QUICK_PROMPTS = [
  'Which projects have no data in the LPO/LOI stage?',
  'Late LPOs by supplier',
  'Open tasks due this week',
];

const promptText = (prompt) => (typeof prompt === 'string' ? prompt : prompt.text);

/** The library for this person: invoicing prompts only with invoicing access. */
export function promptLibraryFor({ invoicing = false } = {}) {
  return PROMPT_LIBRARY
    .filter((group) => invoicing || !group.invoicing)
    .map((group) => ({
      ...group,
      prompts: group.prompts
        .filter((prompt) => invoicing || typeof prompt === 'string' || !prompt.invoicing)
        .map((prompt) => ({ text: promptText(prompt), action: Boolean(prompt.action) })),
    }))
    .filter((group) => group.prompts.length);
}

/** Where the first [placeholder] is, so it can be selected for typing over. */
export function placeholderRange(text) {
  const match = /\[[^\]]+\]/.exec(String(text || ''));
  return match ? [match.index, match.index + match[0].length] : null;
}

/* ------------------------------------------------------------------ history */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const sessionTime = (session) => session?.updated_at || session?.created_at || '';

/** Today / Yesterday / July / June 2025 … (Dubai calendar), newest first. */
export function groupSessions(sessions, { now } = {}) {
  const today = toDateInputValue(now ? new Date(now) : new Date());
  const yesterdayDate = new Date(`${today}T12:00:00Z`);
  yesterdayDate.setUTCDate(yesterdayDate.getUTCDate() - 1);
  const yesterday = yesterdayDate.toISOString().slice(0, 10);
  const currentYear = today.slice(0, 4);
  const groups = [];
  const byKey = {};
  [...(sessions || [])]
    .sort((a, b) => String(sessionTime(b)).localeCompare(String(sessionTime(a))))
    .forEach((session) => {
      const day = toDateInputValue(sessionTime(session));
      let key;
      let label;
      if (!day) {
        key = 'undated';
        label = 'Older';
      } else if (day === today) {
        key = 'today';
        label = 'Today';
      } else if (day === yesterday) {
        key = 'yesterday';
        label = 'Yesterday';
      } else {
        key = day.slice(0, 7);
        const month = MONTHS[Number(day.slice(5, 7)) - 1];
        label = day.slice(0, 4) === currentYear ? month : `${month} ${day.slice(0, 4)}`;
      }
      if (!byKey[key]) {
        byKey[key] = { key, label, items: [] };
        groups.push(byKey[key]);
      }
      byKey[key].items.push(session);
    });
  return groups;
}

/** "21:06 · 6 messages" today, "12 Jul · 8 messages" before. */
export function sessionMeta(session, { now } = {}) {
  const when = sessionTime(session);
  const today = toDateInputValue(now ? new Date(now) : new Date());
  const time = toDateInputValue(when) === today ? formatTime(when) : formatDate(when, { year: 'auto', now });
  const count = Number(session?.message_count);
  return [time, Number.isFinite(count) && count > 0 ? `${count} ${count === 1 ? 'message' : 'messages'}` : null]
    .filter(Boolean).join(' · ');
}

export function filterSessions(sessions, query) {
  const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return sessions || [];
  return (sessions || []).filter((session) => {
    const title = String(session.title || '').toLowerCase();
    return words.every((word) => title.includes(word));
  });
}

/* ------------------------------------------------------------------ sources */

const COLLECTION_NOUNS = {
  projects: ['project', 'projects'],
  tasks: ['task', 'tasks'],
  variations: ['variation', 'variations'],
  boq_items: ['BOQ item', 'BOQ items'],
  site_requests: ['site request', 'site requests'],
  lpos: ['LPO', 'LPOs'],
  delivery_notes: ['delivery note', 'delivery notes'],
  reservations: ['reservation', 'reservations'],
  stock: ['stock item', 'stock items'],
  catalog: ['catalog item', 'catalog items'],
  documents: ['document', 'documents'],
  invoices: ['invoice', 'invoices'],
  variation_invoices: ['variation invoice', 'variation invoices'],
};

const countNoun = (collection, count) => {
  const [one, many] = COLLECTION_NOUNS[collection] || [collection.replace(/_/g, ' '), collection.replace(/_/g, ' ')];
  return `${Number(count).toLocaleString('en-US')} ${count === 1 ? one : many}`;
};

export function providerLabel(provider) {
  const key = String(provider || '').toLowerCase();
  if (key === 'gemini') return 'Gemini';
  if (key === 'app') return 'Sara app';
  return provider ? String(provider) : '';
}

/**
 * "Based on 128 invoices · 63 projects" — the collections the question is about (records loaded,
 * all of them summarised for the model), at most three; the full list goes in `detail`.
 */
export function describeSources(message) {
  const sources = Array.isArray(message?.sources) ? message.sources.filter((item) => item && item.loaded > 0) : [];
  if (!sources.length) return { text: '', detail: '' };
  let picked = sources.filter((item) => item.relevant);
  if (picked.length < 2) {
    const projects = sources.find((item) => item.collection === 'projects');
    if (projects && !picked.includes(projects)) picked = [...picked, projects];
  }
  if (!picked.length) picked = [...sources].sort((a, b) => b.loaded - a.loaded).slice(0, 2);
  const text = `Based on ${picked.slice(0, 3).map((item) => countNoun(item.collection, item.loaded)).join(' · ')}`;
  const detail = sources
    .map((item) => `${countNoun(item.collection, item.loaded)}${item.included && item.included < item.loaded ? ` (${item.included} in detail)` : ''}`)
    .join(', ');
  return { text, detail: `Read: ${detail}` };
}

/** "as of 21:04" today, "as of 12 Jul, 21:04" before. */
export function asOfLabel(value, { now } = {}) {
  if (!value) return '';
  const today = toDateInputValue(now ? new Date(now) : new Date());
  if (toDateInputValue(value) === today) return `as of ${formatTime(value)}`;
  return `as of ${formatDateTime(value, { year: 'auto', now })}`;
}

/* ------------------------------------------------------------------ tables */

const splitPipes = (line) => line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((cell) => cell.trim().replace(/\*\*/g, ''));

/**
 * The first table in an answer: a markdown table, or 3+ list lines with " | " columns
 * ("- INV-001 | City Walk | Submitted | 94,605.00"). null when there is none.
 */
export function extractTable(text) {
  const lines = String(text || '').split(/\r?\n/);
  // Markdown table
  for (let index = 0; index < lines.length - 1; index += 1) {
    if (/^\s*\|.*\|\s*$/.test(lines[index]) && /^\s*\|?\s*:?-{2,}/.test(lines[index + 1])) {
      const headers = splitPipes(lines[index]);
      const rows = [];
      for (let row = index + 2; row < lines.length && /^\s*\|.*\|\s*$/.test(lines[row]); row += 1) {
        rows.push(splitPipes(lines[row]));
      }
      if (rows.length) return { headers, rows };
    }
  }
  // Pipe-separated list items
  const listRows = lines
    .filter((line) => / \| /.test(line))
    .map((line) => line.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, ''))
    .map(splitPipes)
    .filter((cells) => cells.length >= 2);
  if (listRows.length >= 3) {
    const width = Math.max(...listRows.map((cells) => cells.length));
    return { headers: Array.from({ length: width }, (_, i) => `Column ${i + 1}`), rows: listRows };
  }
  return null;
}

export function tableToCsv(table) {
  if (!table) return '';
  return [table.headers, ...table.rows].map(csvRow).join('\r\n');
}

/** Plain-text export of a whole chat. */
export function chatToText(title, messages) {
  const lines = [`${title || 'AI chat'}`, ''];
  (messages || []).forEach((message) => {
    if (message.welcome) return;
    const when = message.created_at ? ` (${formatDateTime(message.created_at)})` : '';
    lines.push(`${message.role === 'user' ? 'You' : 'AI'}${when}:`);
    lines.push(String(message.content || ''));
    lines.push('');
  });
  return lines.join('\n');
}

/* ------------------------------------------------------------------ errors */

/**
 * One friendly sentence instead of raw API text ("Gemini API key is not configured").
 * When no answer came back (timeout, bad gateway, dropped connection) a requested change may already
 * be saved, so the text never claims "nothing was changed". Try again is safe: the page re-sends the
 * same question id and the server returns the stored answer instead of running it twice.
 */
export function friendlyAiError(error) {
  const status = error?.response?.status;
  const detail = String(error?.response?.data?.detail || error?.message || '');
  // BUG-012: the per-person AI budget (backend/rate_limit.py) answers 429 with retry_after (seconds). The server
  // refuses before anything is read, asked or saved, so Try again is always safe.
  if (status === 429) {
    const wait = Math.max(1, Math.ceil(Number(error?.response?.data?.retry_after) || 60));
    const minutes = Math.ceil(wait / 60);
    const when = wait < 60 ? `${wait} second${wait === 1 ? '' : 's'}` : `${minutes} minute${minutes === 1 ? '' : 's'}`;
    return { title: 'Too many AI requests', text: `You have asked the AI a lot in a short time. Wait ${when}, then press Try again.`, retry: true };
  }
  if (status === 503 || /not configured/i.test(detail)) {
    return { title: 'AI is not configured yet', text: 'An admin needs to add the Gemini API key before questions can be answered.', retry: false };
  }
  if (status === 504 || /timed out|timeout/i.test(detail)) {
    return { title: 'Gemini took too long to answer', text: 'Try again, or ask a narrower question. If you asked for a change, Try again will not make it twice.', retry: true };
  }
  if (status === 502) {
    return { title: 'Gemini could not answer right now', text: 'Try again in a moment. If you asked for a change, Try again will not make it twice.', retry: true };
  }
  if (status === 403) {
    return { title: 'Not allowed', text: typeof error?.response?.data?.detail === 'string' ? error.response.data.detail : 'Your role cannot do this.', retry: false };
  }
  if (!error?.response) {
    return { title: 'No connection', text: 'No answer came back. Check the connection and try again — a change you asked for is never made twice.', retry: true };
  }
  return { title: 'Something went wrong', text: 'The question could not be answered. Try again.', retry: true };
}
